import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTenants, useOutbox } from '../hooks/api';
import { Loading, ErrorBox, EmptyBox } from '../components/Loading';
import { Status, statusKindForJobStatus } from '../components/Status';
import { formatNumber, formatTimestamp } from '../api';

const STATUS_FILTERS = ['', 'pending', 'in_progress', 'sent', 'failed', 'dead_letter'];

export default function OutboxPage() {
  const tenants = useTenants();
  const tenantCodes = useMemo(() => tenants.data?.data.map((t) => t.code) ?? [], [tenants.data]);

  const [tenantFilter, setTenantFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [target, setTarget] = useState<string>('');

  const visibleTenants = tenantFilter ? [tenantFilter] : tenantCodes;

  return (
    <div className="space-y-3">
      <h1 className="text-[14px] font-semibold uppercase tracking-wide text-neutral-700 border-b border-neutral-300 pb-1">
        Outbox-Backlog (Cross-Tenant)
      </h1>

      <div className="panel">
        <div className="panel-h">Filter</div>
        <div className="panel-b grid grid-cols-3 gap-3">
          <div>
            <label className="block text-[11px] text-neutral-600 mb-0.5">Tenant</label>
            <select
              className="input w-full"
              value={tenantFilter}
              onChange={(e) => setTenantFilter(e.target.value)}
            >
              <option value="">— alle —</option>
              {tenantCodes.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-neutral-600 mb-0.5">Status</label>
            <select
              className="input w-full"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_FILTERS.map((s) => (
                <option key={s} value={s}>
                  {s || '— alle —'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-neutral-600 mb-0.5">Target-System</label>
            <input
              type="text"
              className="input w-full"
              placeholder="z.B. pulpo, dhl..."
              value={target}
              onChange={(e) => setTarget(e.target.value)}
            />
          </div>
        </div>
      </div>

      {tenants.isLoading && <Loading />}
      {tenants.error && <ErrorBox error={tenants.error} />}

      {visibleTenants.map((code) => (
        <TenantOutboxBlock
          key={code}
          tenantCode={code}
          {...(statusFilter ? { status: statusFilter } : {})}
          {...(target ? { target } : {})}
        />
      ))}

      {visibleTenants.length === 0 && <EmptyBox>Keine Tenants ausgewaehlt.</EmptyBox>}
    </div>
  );
}

function TenantOutboxBlock({
  tenantCode,
  status,
  target,
}: {
  tenantCode: string;
  status?: string;
  target?: string;
}) {
  const filter = {
    ...(status ? { status } : {}),
    ...(target ? { target } : {}),
    limit: 50,
    offset: 0,
  };
  const outbox = useOutbox(tenantCode, filter);

  return (
    <div className="panel">
      <div className="panel-h flex items-center justify-between">
        <span>{tenantCode}</span>
        <span className="text-[10px] text-neutral-500 font-mono">
          {outbox.data ? `${outbox.data.data.length}/${outbox.data.total}` : '–'}
        </span>
      </div>
      <div className="panel-b p-0">
        {outbox.isLoading && (
          <div className="p-2">
            <Loading />
          </div>
        )}
        {outbox.error && (
          <div className="p-2">
            <ErrorBox error={outbox.error} />
          </div>
        )}
        {outbox.data && outbox.data.data.length === 0 && (
          <div className="p-2">
            <EmptyBox>Keine Events fuer diesen Filter.</EmptyBox>
          </div>
        )}
        {outbox.data && outbox.data.data.length > 0 && (
          <table className="tbl">
            <thead>
              <tr>
                <th>Erstellt</th>
                <th>Source</th>
                <th>Target</th>
                <th>Event</th>
                <th>Status</th>
                <th className="num">HTTP</th>
                <th className="num">Tries</th>
                <th>Fehler</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {outbox.data.data.map((r) => (
                <tr key={r.id}>
                  <td className="mono">{formatTimestamp(r.created_at)}</td>
                  <td className="mono">{r.source_tool}</td>
                  <td className="mono">{r.target_system}</td>
                  <td className="mono">{r.event_type}</td>
                  <td>
                    <Status kind={statusKindForJobStatus(r.status)}>{r.status}</Status>
                  </td>
                  <td className="num">{r.http_status ?? ''}</td>
                  <td className="num">{formatNumber(r.attempts)}</td>
                  <td className="text-[11px] text-st-err max-w-[300px] truncate">
                    {r.last_error ?? ''}
                  </td>
                  <td>
                    <Link
                      to={`/tenants/${encodeURIComponent(tenantCode)}`}
                      className="text-[11px] underline"
                    >
                      Tenant
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
